import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Configure axios defaults - use environment variable or fallback to relative path for domain
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
console.log('API Base URL:', API_BASE_URL);
axios.defaults.baseURL = API_BASE_URL;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [userType, setUserType] = useState(null); // 'admin', 'client', or 'user'

  useEffect(() => {
    const storedToken = localStorage.getItem('adminToken');
    const storedIsAdmin = localStorage.getItem('isAdmin') === 'true';
    const storedClientId = localStorage.getItem('clientId');
    const storedUserType = localStorage.getItem('userType');

    if (storedToken) {
      console.log('Restoring token from storage:', storedToken);
      setToken(storedToken);
      setIsAuthenticated(true);
      setIsAdmin(storedIsAdmin);
      setClientId(storedClientId);
      setUserType(storedUserType);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    setLoading(false);
  }, []);

  const loginAsAdmin = async (username, password) => {
    try {
      const credentials = btoa(`${username}:${password}`);
      console.log('Attempting admin login with credentials:', credentials);
      
      const response = await axios.post('/admin/login', null, {
        headers: {
          'Authorization': `Basic ${credentials}`
        }
      });
      
      const { access_token } = response.data;
      console.log('Received access token:', access_token);
      
      localStorage.setItem('adminToken', access_token);
      localStorage.setItem('isAdmin', 'true');
      localStorage.setItem('userType', 'admin');
      localStorage.removeItem('clientId');
      
      setToken(access_token);
      setIsAuthenticated(true);
      setIsAdmin(true);
      setClientId(null);
      setUserType('admin');
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      console.log('Set axios default headers:', axios.defaults.headers.common);
      
      return true;
    } catch (error) {
      console.error('Admin login failed:', error.response?.data || error.message);
      return false;
    }
  };

  const loginWithClientToken = async (clientToken) => {
    try {
      console.log('Attempting client token login with token:', clientToken);
      // Verify the token by making a test request
      const response = await axios.get('/users/get-all-users', {
        headers: {
          'Authorization': `Bearer ${clientToken}`
        }
      });
      
      localStorage.setItem('adminToken', clientToken);
      localStorage.setItem('isAdmin', 'false');
      localStorage.setItem('clientId', response.data.client_id);
      localStorage.setItem('userType', 'client');
      
      setToken(clientToken);
      setIsAuthenticated(true);
      setIsAdmin(false);
      setClientId(response.data.client_id);
      setUserType('client');
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${clientToken}`;
      console.log('Set axios default headers for client:', axios.defaults.headers.common);
      
      return true;
    } catch (error) {
      console.error('Client token login failed:', error.response?.data || error.message);
      return false;
    }
  };

  const loginWithUserToken = async (clientToken) => {
    try {
      console.log('Attempting client token login with token:', clientToken);
      // Verify the token by making a test request
      const response = await axios.get('/users/get-all-users', {
        headers: {
          'Authorization': `Bearer ${clientToken}`
        }
      });
      
      localStorage.setItem('adminToken', clientToken);
      localStorage.setItem('isAdmin', 'false');
      localStorage.setItem('clientId', response.data.client_id);
      localStorage.setItem('userType', 'user');
      
      setToken(clientToken);
      setIsAuthenticated(true);
      setIsAdmin(false);
      setClientId(response.data.client_id);
      setUserType('user');
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${clientToken}`;
      console.log('Set axios default headers for user:', axios.defaults.headers.common);
      
      return true;
    } catch (error) {
      console.error('User token login failed:', error.response?.data || error.message);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('clientId');
    localStorage.removeItem('userType');
    setToken(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    setClientId(null);
    setUserType(null);
    delete axios.defaults.headers.common['Authorization'];
    console.log('Cleared authentication state');
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      token, 
      isAdmin, 
      clientId,
      userType,
      loginAsAdmin, 
      loginWithClientToken,
      loginWithUserToken,
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 